import React from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faFolderBookmark, faFolderOpen } from '@fortawesome/pro-solid-svg-icons';
import { CodeSetItem } from '../CodeSetItem';

interface FolderSectionProps {
  folder: any;
  isExpanded: boolean;
  onToggle: (folderId: string) => void;
  getCodeSetInstanceCount: (itemId: string) => number;
  handleItemSelection: (item: any) => void;
  allowMultiple: boolean;
  isLimitReached: boolean;
  handleModeToggle?: (item: any, mode: 'treating' | 'sending') => void;
  getActiveModesForCodeSet?: (itemId: string) => ('treating' | 'sending')[];
  isReferringDisabled?: boolean;
}

/**
 * FolderSection component - Renders a collapsible folder with code set items
 */
export const FolderSection: React.FC<FolderSectionProps> = ({
  folder,
  isExpanded,
  onToggle,
  getCodeSetInstanceCount,
  handleItemSelection,
  allowMultiple,
  isLimitReached,
  handleModeToggle,
  getActiveModesForCodeSet,
  isReferringDisabled = false,
}) => {
  return allowMultiple ? (
    <>
      {/* Folder Header - spans all grid columns */}
      <div
        className="col-span-full h-10 px-4 rounded-lg flex items-center gap-2 cursor-pointer hover:bg-neutral-100"
        onClick={() => onToggle(folder.id?.toString())}
        data-testid={`folder-header-${folder.id}`}
      >
        <div className="w-5 h-5 overflow-hidden flex items-center justify-center">
          <Icon
            icon={isExpanded ? faFolderOpen : faFolderBookmark}
            className="w-4 h-3.5 text-neutral-600"
          />
        </div>
        <div className="flex-1 justify-start text-neutral-700 text-sm font-medium font-['Inter'] leading-normal">
          {folder.label}
        </div>
      </div>

      {/* Folder Items - each CodeSetItem participates in the grid */}
      {isExpanded &&
        folder.items.map((item: any, index: number) => {
          const instanceCount = getCodeSetInstanceCount(item.id);
          const isMaxedOut = allowMultiple && instanceCount >= 2;
          const isDisabled = isLimitReached && instanceCount === 0;

          return (
            <CodeSetItem
              key={`${item.id}-${index}`}
              item={item}
              instanceCount={instanceCount}
              isMaxedOut={isMaxedOut}
              isDisabled={isDisabled}
              onSelect={handleItemSelection}
              allowMultiple={allowMultiple}
              onModeToggle={handleModeToggle}
              activeModes={getActiveModesForCodeSet?.(item.id) || []}
              isLimitReached={isLimitReached}
              isReferringDisabled={isReferringDisabled}
            />
          );
        })}
    </>
  ) : (
    <div className="self-stretch w-full">
      {/* Folder Header */}
      <div
        className="group self-stretch w-full h-10 px-4 rounded-lg inline-flex justify-start items-center gap-2 cursor-pointer hover:bg-neutral-100"
        onClick={() => onToggle(folder.id?.toString())}
        data-testid={`folder-header-${folder.id}`}
      >
        <div className="w-5 h-5 overflow-hidden flex items-center justify-center">
          <Icon
            icon={isExpanded ? faFolderOpen : faFolderBookmark}
            className="w-4 h-3.5 text-neutral-600"
          />
        </div>
        <div className="flex-1 justify-start text-neutral-700 text-sm font-medium font-['Inter'] leading-normal">
          {folder.label}
        </div>
      </div>

      {/* Folder Items */}
      {isExpanded && (
        <div className="px-2 pb-2 flex flex-col">
          {folder.items.map((item: any, index: number) => {
            const instanceCount = getCodeSetInstanceCount(item.id);
            const isMaxedOut = allowMultiple && instanceCount >= 2;
            const isDisabled = isLimitReached && instanceCount === 0;

            return (
              <CodeSetItem
                key={`${item.id}-${index}`}
                item={item}
                instanceCount={instanceCount}
                isMaxedOut={isMaxedOut}
                isDisabled={isDisabled}
                onSelect={handleItemSelection}
                allowMultiple={allowMultiple}
                onModeToggle={handleModeToggle}
                activeModes={getActiveModesForCodeSet?.(item.id) || []}
                isLimitReached={isLimitReached}
                isReferringDisabled={isReferringDisabled}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FolderSection;
